import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { NotificationService } from '@progress/kendo-angular-notification';
import { IntlService } from '@progress/kendo-angular-intl';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SessionService } from '../core/session.service';
import { ICreateCustomerSession } from '../core/payment/create-customer-session.interface';
import { PaymentService } from '../core/payment/payment.service';
import { ICreateCustomerSessionResponse } from '../core/payment/create-customer-session-response.interface';
import { ISession } from '../core/session.interface';
import { LANGUAGES, LanguageService } from '../core/language.service';
import { STRINGS } from '../../strings';
import { ThemeService } from '../core/theme.service';
import { environment } from '../../environments/environment';
import { FeeType } from '../core/fee.type';
import {
  StripeElements,
  StripeElementsOptions,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { StripeService } from 'ngx-stripe';
import { ICallbackResult } from '../core/callback-result.interface';
import { IPaymentConfirmationResponse } from '../core/payment/payment-confirmation-response.interface';
import {
  DialogComponent,
  DialogTitleBarComponent,
} from '@progress/kendo-angular-dialog';
import { SVGIcon } from '@progress/kendo-svg-icons';
import {
  CheckBoxState,
  CheckBoxComponent,
} from '@progress/kendo-angular-inputs';
import { IPaymentAgreementResponse } from '../core/payment/payment-agreement-response.interface';
import { BehaviorSubject, filter, finalize, of, throwError } from 'rxjs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { NgIf, NgClass, AsyncPipe } from '@angular/common';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { InputFieldComponent } from '../input-field.component';
import { LabelComponent } from '@progress/kendo-angular-label';
import { catchError, mergeMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-payment',
  templateUrl: './tokenization-stripe.component.html',
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./tokenization-stripe.component.scss', './custom-styles.scss'],
  standalone: true,
  imports: [
    TooltipDirective,
    NgIf,
    DialogComponent,
    DialogTitleBarComponent,
    SVGIconComponent,
    ButtonComponent,
    LoaderComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    InputFieldComponent,
    CheckBoxComponent,
    LabelComponent,
    AsyncPipe,
  ],
})
export class TokenizationStripeComponent implements OnInit, AfterViewInit {
  @Input() embedId = '';
  @Input() amount = '0';
  @Input() renderTarget = '';

  /* eslint-disable @angular-eslint/no-input-rename */
  @Input('callback') attrCallback: keyof typeof window = 'alert';
  @Input('policy-id') attrPolicyId = '';
  @Input('customer-id') attrCustomerId = '';
  @Input('first-name') attrFirstName = '';
  @Input('last-name') attrLastName = '';
  @Input('insured-name') attrInsuredName = '';
  @Input('email') attrEmail = '';
  @Input('address') attrAddress = '';
  @Input('city') attrCity = '';
  @Input('province') attrProvince = '';
  @Input('zip') attrZip = '';
  @Input('phone') attrPhone = '';
  @Input('custom-pif') attrCustomPif = '';
  @Input('custom-dp') attrCustomDp = '';
  @Input('custom-pmt') attrCustomPmt = '';
  @Input('custom-programcode') attrCustomProgramcode = '';
  @Input('custom-balancedue') attrCustomBalancedue = '';
  @Input('tnc-url') tncUrl = '';
  @Input('priv-url') privUrl = '';
  @Input('language') attrLanguage = '';
  @Input('reference-number') attrReferenceNumber = '';
  @Input('theme') theme = 'default';
  /* eslint-enable @angular-eslint/no-input-rename */
  @ViewChild('notificationContainer', { read: ViewContainerRef })
  public notificationContainer?: ViewContainerRef = undefined;
  @ViewChild('rootElement', { read: ViewContainerRef })
  public rootElement?: ViewContainerRef = undefined;

  public elementsOptions: StripeElementsOptions = {
    locale: 'en',
    fonts: [
      {
        cssSrc: 'https://embed.insurepay.com/inter.css',
      },
    ],

    appearance: {
      theme: 'flat',

      variables: {
        colorPrimary: '#e01719',
        colorBackground: '#fff',
        fontFamily: '"Inter", sans-serif',
        borderRadius: '0',
        colorText: '#212f29',
        fontSizeBase: '16px',
        fontSizeSm: '16px',
        gridRowSpacing: '32px',
        gridColumnSpacing: '16px',
        focusOutline: 'rgba(0, 0, 0, 0.16)',
        focusBoxShadow: '0 0 0 2px color-mix(in srgb, #3d3d3d 8%, transparent)',
      },

      rules: {
        '.Label': {
          marginBottom: '9px',
        },
        '.Input': {
          border: '1px solid #0000002A',
          paddingTop: '17px',
          paddingBottom: '17px',
        },
        '.Error': {
          marginBottom: '-22px',
        },
      },
    },
  };

  public paymentElementOptions: StripePaymentElementOptions = {
    terms: {
      card: 'never',
    },
  };

  public paymentIntentId: string = '';
  public confirmationToken: string = '';

  public stripeElements?: StripeElements;

  public dialogOpened$ = new BehaviorSubject<boolean>(false);
  public form: FormGroup = new FormGroup({
    amount: new FormControl(this.amount),
    referenceNumber: new FormControl(),
  });
  public loading$ = new BehaviorSubject<boolean>(false);
  public complete = false;
  public stripeStep$ = new BehaviorSubject<boolean>(false);
  public stripeFinal$ = new BehaviorSubject<boolean>(false);
  public session?: ISession = undefined;
  private _initSessionPromise?: Promise<void>;
  public clientInformation?: ISession['merchant']['clientInformation'] =
    undefined;
  public billingInformation?: ISession['merchant']['billingInformation'] =
    undefined;
  public originalPolicyId?: string = undefined;
  public policyId?: ISession['merchant']['policyId'] = undefined;
  public paymentLabel?: ISession['merchant']['paymentLabel'] = undefined;
  public fee?: ISession['merchant']['fee'] = undefined;
  public disclaimer?: ISession['merchant']['tokenizationMessage']['registrationMessage'] =
    undefined;
  public extraMessage?: ISession['merchant']['extraMessage'] = undefined;
  public extraFooterMessage?: ISession['merchant']['extraFooterMessage'] =
    undefined;
  public zip: any = undefined;

  public isEmailInvalid = false;

  public stashedPolicyId = '';
  public stashedCustom?: Record<string, any>;

  public environment = environment;

  public currentLanguage = LANGUAGES.english;

  public icons = {
    userIcon: {
      viewBox: '0 0 32 32',
      name: 'user-icon',
      content:
        '<path d="M24.52 19.4133C22.28 18.2667 19.3733 17.3333 16 17.3333C12.6267 17.3333 9.72001 18.2667 7.48001 19.4133C6.14668 20.0933 5.33334 21.4667 5.33334 22.96V24C5.33334 25.4667 6.53334 26.6667 8.00001 26.6667H24C25.4667 26.6667 26.6667 25.4667 26.6667 24V22.96C26.6667 21.4667 25.8533 20.0933 24.52 19.4133Z"/><path d="M13.04 16H18.96C20.5734 16 21.8134 14.5867 21.6 12.9867L21.1734 9.72001C20.76 7.18668 18.56 5.33334 16 5.33334C13.44 5.33334 11.24 7.18668 10.8267 9.72001L10.4 12.9867C10.1867 14.5867 11.4267 16 13.04 16Z"/>',
    },
    walletIcon: {
      viewBox: '0 0 32 32',
      name: 'wallet-icon',
      content:
        '<path d="M13.3333 21.3333V10.6667C13.3333 9.2 14.52 8 16 8H28V6.66667C28 5.2 26.8 4 25.3333 4H6.66667C5.18667 4 4 5.2 4 6.66667V25.3333C4 26.8 5.18667 28 6.66667 28H25.3333C26.8 28 28 26.8 28 25.3333V24H16C14.52 24 13.3333 22.8 13.3333 21.3333ZM17.3333 10.6667C16.6 10.6667 16 11.2667 16 12V20C16 20.7333 16.6 21.3333 17.3333 21.3333H29.3333V10.6667H17.3333ZM21.3333 18C20.2267 18 19.3333 17.1067 19.3333 16C19.3333 14.8933 20.2267 14 21.3333 14C22.44 14 23.3333 14.8933 23.3333 16C23.3333 17.1067 22.44 18 21.3333 18Z"/>',
    } as SVGIcon,
    tooltipIcon: {
      viewBox: '0 0 20 20',
      name: 'tooltip-icon',
      content:
        '<path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM9 14H11V16H9V14ZM10.61 4.04C8.55 3.74 6.73 5.01 6.18 6.83C6 7.41 6.44 8 7.05 8H7.25C7.66 8 7.99 7.71 8.13 7.33C8.45 6.44 9.4 5.83 10.43 6.05C11.38 6.25 12.08 7.18 12 8.15C11.9 9.49 10.38 9.78 9.55 11.03C9.55 11.04 9.54 11.04 9.54 11.05C9.53 11.07 9.52 11.08 9.51 11.1C9.42 11.25 9.33 11.42 9.26 11.6C9.25 11.63 9.23 11.65 9.22 11.68C9.21 11.7 9.21 11.72 9.2 11.75C9.08 12.09 9 12.5 9 13H11C11 12.58 11.11 12.23 11.28 11.93C11.3 11.9 11.31 11.87 11.33 11.84C11.41 11.7 11.51 11.57 11.61 11.45C11.62 11.44 11.63 11.42 11.64 11.41C11.74 11.29 11.85 11.18 11.97 11.07C12.93 10.16 14.23 9.42 13.96 7.51C13.72 5.77 12.35 4.3 10.61 4.04Z" />',
    },
  };
  public logo = 'https://embed.insurepay.com/insurepay-logo.png';

  public stripeFee = -1;
  public stripeAmountCents = -1;
  public isAgreementChecked = false;
  public isBifurcatedAgreementChecked = false;
  public canFinalizePayment$ = new BehaviorSubject<boolean>(false);
  public brand: string = '';
  public identifier: string = '';
  public notificationTracker:  { [key: string]: any } = {};

  constructor(
    private sessionService: SessionService,
    private paymentService: PaymentService,
    private notificationService: NotificationService,
    private languageService: LanguageService,
    private intl: IntlService,
    private themeService: ThemeService,
    private stripe: StripeService,
  ) {}

  public callback = (_: ICallbackResult | undefined) => {};

  public ngOnInit(): void {
    console.log(this.icons.userIcon);
    if (this.renderTarget !== '') {
      const button = document.getElementById(
        this.renderTarget,
      ) as HTMLButtonElement;

      button.addEventListener('click', () =>
        // @ts-ignore
        this.open({}, window[this.attrCallback]),
      );
    }

    this.loading$.next(true);
    this.sessionService.initSession(this.embedId);
    this.sessionService.getSession().subscribe((session) => {
      if (session) {
        this.initSession(session);
      }
    });
    this.form
      .get('amount')
      ?.setValue(this.intl.parseNumber(this.amount.toString()).toFixed(2));
    this.form.get('policyId')?.setValue(this.stashedPolicyId);

    // @ts-ignore
    window.acordEmbed = {
      open: this.open.bind(this),
    };

    const readyEvent = new Event('acordembedready');
    document.dispatchEvent(readyEvent);
  }

  public ngAfterViewInit() {
    if (this.rootElement) {
      this.themeService.loadTheme(this.rootElement, this.theme);
      const element =
        window.document.getElementsByTagName('acord-tokenization')[0];

      const stripeContainer = window.document.createElement('div');
      stripeContainer.slot = 'stripe-container';

      const stripePaymentContainer = window.document.createElement('div');
      stripePaymentContainer.id = 'stripe-payment-container';

      const stripeLinkContainer = window.document.createElement('div');
      stripeLinkContainer.id = 'stripe-link-container';

      stripeContainer.appendChild(stripeLinkContainer);
      stripeContainer.appendChild(stripePaymentContainer);

      element.appendChild(stripeContainer);
    }
  }

  public getString(key: keyof typeof STRINGS, ...args: Array<string>) {
    return this.languageService.getString(key, ...args);
  }

  public checkEmailValidity(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value !== 'string') {
        return { emailInvalid: 'Email is required' };
      }

      const value = control.value;

      if (value.includes('@') && value.includes('.')) {
        const atCount = value.split('@').length;
        const dotCount = value.split('.').length;

        if (atCount > 2 || dotCount > 2) {
          // Semicolon splits email addresses, should be one greater @ and . than ;
          if (!value.includes('@@') && !value.includes('..')) {
            this.isEmailInvalid = false;
            return null;
          }
        } else {
          this.isEmailInvalid = false;
          return null;
        }
      }

      this.isEmailInvalid = true;
      return { emailInvalid: 'Invalid email address' };
    };
  }

  public initSession(session?: ISession): Promise<void> {
    if (!this._initSessionPromise) {
      this._initSessionPromise = this._doInitSession(session);
    }
    return this._initSessionPromise;
  }

  private async _doInitSession(session?: ISession) {
    if (!session) {
      await new Promise((res, _rej) => {
        this.sessionService.getSession().subscribe((newSession) => {
          if (newSession) {
            session = newSession;
            res(session);
          }
        });
      });
    }

    session = session as ISession;

    this.stripe.setKey(
      session.region === 'USA'
        ? environment.stripeKeyUS
        : environment.stripeKeyCA,
    );


    this.session = session;
    this.clientInformation = session.merchant.clientInformation;
    this.billingInformation = session.merchant.billingInformation;
    this.policyId = session.merchant.policyId;
    this.paymentLabel = session.merchant.paymentLabel;
    this.fee = session.merchant.fee;
    this.disclaimer = session.merchant.disclaimer;
    this.extraMessage = session.merchant.extraMessage;
    this.extraFooterMessage = session.merchant.extraFooterMessage;

    const formGroupConfig: { [key: string]: FormControl } = {
      amount: new FormControl(this.amount.toString()),
      referenceNumber: new FormControl(),
    };

    if (this.policyId.show || this.policyId.required) {
      formGroupConfig.policyId = this.policyId.required
        ? new FormControl(null, [Validators.required])
        : new FormControl();
    }

    Object.entries(this.clientInformation).forEach(([key, value]) => {
      if (typeof value === 'boolean') return;

      if (value.show || value.required) {
        if (key === 'email') {
          formGroupConfig[key] = new FormControl(null, [
            Validators.required,
            this.checkEmailValidity(),
          ]);
        } else {
          formGroupConfig[key] = value.required
            ? new FormControl(null, [Validators.required])
            : new FormControl();
        }
      }
    });

    this.form = new FormGroup(formGroupConfig);

    this.loading$.next(false);
  }

  public createCustomerSession(): void {
    this.form.markAllAsTouched();
    this.loading$.next(true);

    if (this.form.invalid) {
      this.loading$.next(false);
      return;
    }

    const results = this.form.value as ICreateCustomerSession;

    if (this?.policyId?.omit.allow && this.policyId?.omit.quantity) {
      results.policyId = this.originalPolicyId || results.policyId;
    }

    results.isTokenize = true;

    this.paymentService
      .createCustomerSession(results)
      .pipe(
        mergeMap((result) => {
          if (!result?.clientSecret) {
            this.callback({
              status: 'ERROR',
              message: 'Unknown error occurred, please try again',
              errorType: 'SYSTEM',
            });
            this.close();
            throw new Error('unable to setup stripe customer session');
          }

          return of(result as ICreateCustomerSessionResponse);
        }),
        mergeMap((result) => {
          this.paymentIntentId = result.paymentIntentId;
          this.elementsOptions.clientSecret = result.clientSecret;
          this.elementsOptions.customerSessionClientSecret =
            result.customerSessionClientSecret;

          return of(this.elementsOptions);
        }),
        mergeMap((elementsOptions) => this.stripe.elements(elementsOptions)),
        tap((elements) => {
          const paymentElement = elements.create(
            'payment',
            this.paymentElementOptions,
          );

          paymentElement.mount('#stripe-payment-container');

          this.stripeElements = elements;
          this.stripeStep$.next(true);
        }),
        catchError((err) => {
          this.displayNotification(
            err?.message || err || 'unknown error, please try again later',
          );
          console.error(err?.message || err);
          this.stripeStep$.next(false);

          throw err;
        }),
        finalize(() => {
          this.loading$.next(false);
        }),
      )
      .subscribe(() => console.log('customer session created'));
  }

  public async submitAgreement(event: Event) {
    event.preventDefault();

    this.loading$.next(true);

    this.paymentService
      .paymentTokenize({
        paymentIntentId: this.paymentIntentId,
        confirmationToken: this.confirmationToken,
      })
      .pipe(
        mergeMap((result) => {
          if (!result?.status) {
            this.displayNotification('unknown error, please try again later');
            this.stripeFinal$.next(false);
            throw new Error('could not submit agreements');
          }

          return of(result as IPaymentAgreementResponse);
        }),
        tap((result) => {
          if (result.status === 'succeeded' || result.status === 'processing') {
            this.callback({
              status: 'SUCCESS',
              message: 'APPROVED',
              transactionId: result.paymentIntent,
            });
            this.close();
          } else {
            this.displayNotification(
              result?.detail || 'unknown error, please try again later',
            );
            this.stripeFinal$.next(false);
          }
        }),
        finalize(() => this.loading$.next(false)),
      )
      .subscribe(() => console.log('agreement submitted'));
  }

  public agreementChecked(state: CheckBoxState) {
    this.isAgreementChecked = !!state.valueOf();
    this.checkAgreements();
  }

  private checkAgreements() {
    this.canFinalizePayment$.next(
      this.isAgreementChecked && this.stripeFinal$.value,
    );
  }

  public async stripeSubmit(event: Event) {
    event.preventDefault();
    // Early guard, this situation shouldn't happen
    if (!this.stripeElements) return;
    this.hideNotifications();

    this.loading$.next(true);

    const { error: submitError } = await this.stripeElements.submit();
    if (submitError) {
      this.displayNotification(
        submitError.message || 'unknown error, please try again later',
      );
      this.loading$.next(false);
      return;
    }

    this.stripe
      .createConfirmationToken({
        elements: this.stripeElements,
      })
      .pipe(
        mergeMap(({ error, confirmationToken }) => {
          if (error) {
            this.displayNotification(
              error.message || 'unknown error, please try again later',
            );

            throw new Error('error creating confirmation token');
          }

          return of(confirmationToken?.id);
        }),
        tap(
          (confirmationToken) => (this.confirmationToken = confirmationToken),
        ),
        mergeMap((confirmationToken) =>
          this.paymentService.paymentConfirmation({
            paymentIntentId: this.paymentIntentId,
            confirmationToken,
          }),
        ),
        mergeMap((confirmResult) => {
          if (!confirmResult?.status) {
            this.displayNotification('unknown error, please try again later');
            this.loading$.next(false);

            throw new Error('error submitting confirmation token to api');
          }

          return of(confirmResult as IPaymentConfirmationResponse);
        }),
        mergeMap((confirmResult) => {
          if (confirmResult.status === 'succeeded') {
            this.identifier = confirmResult.identifier;
            this.brand = confirmResult.brand;

            this.stripeFee = confirmResult.fee;
            this.stripeAmountCents = confirmResult.amountCents;
            this.stripeFinal$.next(true);
            this.checkAgreements();
            return of(undefined);
          } else if (confirmResult.status === 'requires_action') {
            return this.stripe.handleNextAction({
              clientSecret: this.elementsOptions.clientSecret || '',
            });
          } else {
            this.displayNotification(
              confirmResult?.detail || 'unknown error, please try again later',
            );
            return of(undefined);
          }
        }),
        filter((isNextAction) => typeof isNextAction !== 'undefined'),
        tap((nextActionResult) => {
          if (nextActionResult?.error) {
            this.displayNotification(
              nextActionResult?.error?.message ||
                'unknown error, please try again later',
            );

            throw new Error('failed completing next action');
          }

          this.stripeFinal$.next(true);
          this.checkAgreements();
        }),
        finalize(() => this.loading$.next(false)),
      )
      .subscribe(() => console.log('submitted confirmation'));
  }

  public showPaymentMethod(event: Event) {
    event.preventDefault();

    this.createCustomerSession();
    this.stripeFinal$.next(false);
    this.checkAgreements();
  }

  public onTextBlur = (input: any) => {
    this.form.patchValue({
      [input.control.name]: input.value.trimStart().trimEnd(),
    });
    this.form.controls[input.control.name].markAllAsTouched();
  };

  public close() {
    this.dialogOpened$.next(false);
    this.form.reset();
    this.reset();
  }

  private reset() {
    this.stripeStep$.next(false);
    this.stripeFinal$.next(false);
    this.stripeFee = -1;
    this.stripeAmountCents = -1;
  }

  public cancel() {
    if (!this.loading$.value && !this.complete) {
      this.dialogOpened$.next(false);
      this.callback({ status: 'CANCEL' });
      this.reset();
    } else if (this.complete) {
      this.dialogOpened$.next(false);
    }
  }

  public shouldDisplayClientInfo(
    key: keyof ISession['merchant']['clientInformation'],
  ) {
    const field = this.clientInformation
      ? this.clientInformation[key]
      : undefined;

    if (!field || typeof field === 'boolean') return false;

    return field?.show || field?.required || false;
  }

  public getFieldConfig(key: keyof ISession['merchant']['clientInformation']) {
    const field = this.clientInformation
      ? this.clientInformation[key]
      : undefined;

    if (!field || typeof field === 'boolean')
      return {
        en: 'MISSING TRANSLATION',
        fr: 'MISSING TRANSLATION',
        required: true,
        readOnly: false,
      };

    return {
      ...field,
      en: field?.label?.en || 'MISSING TRANSLATION',
      fr: field?.label?.fr || 'MISSING TRANSLATION',
    };
  }

  public getAmount(): string {
    return this.toCurrency(
      parseFloat(this.form?.controls?.amount?.value.toString() || '0'),
    );
  }

  public getAmountCents(): number {
    const amountString = parseFloat(
      this.form?.controls?.amount?.value.toString() || '0',
    ).toFixed(2);
    return parseInt(amountString.replace('.', ''), 10);
  }

  public getFee(): string {
    return this.toCurrency(this.getFeeCents() / 100);
  }

  public getStripeFee(): string {
    return this.toCurrency(this.stripeFee / 100);
  }

  public calculatePercent(amountCents: number): number {
    return amountCents * ((this.fee?.value?.percent || 0) / 100 || 0);
  }

  public calculateAmount(): number {
    return Math.floor((this.fee?.value?.amount || 0) * 100);
  }

  public getFeeCents(): number {
    const amountCents = this.getAmountCents();
    if (!this.fee || !this.fee.status) return 0;
    switch (this.fee.selectedFeeType) {
      case FeeType.range:
        const rangeCents = parseInt(
          this.fee.value.range.toFixed(2).replace('.', ''),
          10,
        );
        if (amountCents >= rangeCents) {
          return this.calculatePercent(amountCents);
        } else {
          return this.calculateAmount();
        }
      case FeeType.amount:
        return this.calculateAmount();
      case FeeType.percent:
        return this.calculatePercent(amountCents);
      default:
        return 0;
    }
  }

  public getTotal(): string {
    const amountCents = this.getAmountCents();
    let feeCents = 0;
    if (this.shouldDisplayFee()) {
      feeCents = this.getFeeCents();
    }

    return this.toCurrency((amountCents + feeCents) / 100);
  }

  public getStripeTotal(): string {
    const amountCents = this.stripeAmountCents;
    let feeCents = this.stripeFee;

    return this.toCurrency((amountCents + feeCents) / 100);
  }

  public async open(
    properties?: { [key: string]: string | { [key: string]: string } },
    callback?: () => void,
  ) {
    if (properties?.amount) properties.amount = properties.amount.toString();
    properties = Object.assign(
      {
        amount: this.amount.toString(),
        policyId: this.attrPolicyId,
        language: this.attrLanguage || 'en',
        referenceNumber: this.attrReferenceNumber,
        clientInformation: {
          firstName: this.attrFirstName,
          lastName: this.attrLastName,
          insuredName: this.attrInsuredName,
          email: this.attrEmail,
          address: this.attrAddress,
          city: this.attrCity,
          province: this.attrProvince,
          zip: this.attrZip,
          customerId: this.attrCustomerId,
        },
        custom: {
          pif: this.attrCustomPif,
          dp: this.attrCustomDp,
          pmt: this.attrCustomPmt,
          programcode: this.attrCustomProgramcode,
          balancedue: this.attrCustomBalancedue,
        },
      },
      properties || {},
    );

    await this.initSession(this.session);

    if (!!callback) {
      this.callback = callback;
    }

    Object.entries(properties).forEach(([key, value]) => {
      if (typeof value !== 'string') return;

      if (key === 'policyId') {
        if (this?.policyId?.omit.allow && this.policyId?.omit.quantity) {
          this.originalPolicyId = value as string;
          value = value.slice(this.policyId.omit.quantity);
        }
        this.stashedPolicyId = value;
      }

      if (key === 'language') {
        this.currentLanguage = value as LANGUAGES;
        this.languageService.setLanguage(value as LANGUAGES);
      }

      this.form.get(key)?.setValue(value);
      if (key === 'amount') this.amount = value.toString();
    });

    if (typeof properties.clientInformation === 'object') {
      Object.entries(properties.clientInformation).forEach(([key, value]) => {
        this.form.get(key)?.setValue(value);
      });
    }

    if (typeof properties.custom === 'object') {
      this.stashedCustom = properties.custom;
      this.form.addControl('custom', new FormControl(properties.custom));
    }

    this.dialogOpened$.next(true);

    if (this.rootElement) {
      const scrollElement =
        this.rootElement.element.nativeElement.querySelector(
          '.k-window.k-dialog .k-window-content.k-dialog-content',
        );
      if (scrollElement) {
        scrollElement.scroll(0, 0);
      }
    }
  }

  public translate = (field: { en?: string; fr?: string } | undefined) => {
    if (this.currentLanguage === LANGUAGES.french)
      return field?.fr || field?.en || 'MISSING TRANSLATION';
    return field?.en || 'MISSING TRANSLATION';
  };

  public setLanguage(language: string) {
    this.currentLanguage = language as LANGUAGES;
    this.languageService.setLanguage(language as LANGUAGES);
  }

  public shouldDisplayFee() {
    return this.fee?.status;
  }

  public showClient(event: any) {
    event.preventDefault();

    if (!this.loading$.value) {
      this.stripeStep$.next(false);
      this.stripeFinal$.next(false);
      this.checkAgreements();
    }
  }

  private toCurrency(value: number) {
    return this.intl.formatNumber(value, 'c', 'en');
  }

  private displayNotification(content: string) {
    let notificationRef = this.notificationService.show({
      content,
      appendTo: this.notificationContainer,
      position: { horizontal: 'center', vertical: 'bottom' },
      type: { style: 'info', icon: true },
    });
    let notificationId = notificationRef?.notification?.instance?.contentId ?? "typescripthell";
    this.notificationTracker[notificationId] = notificationRef;
    notificationRef?.afterHide?.subscribe(() => delete this.notificationTracker[notificationId]);   
    setTimeout(() => notificationRef.hide(), 5000);
  }

  private hideNotifications() { 
    Object.keys(this.notificationTracker).forEach(key => {
      if (this.notificationTracker[key]?.hide) {
        this.notificationTracker[key]?.hide();
      }
    })
  }

  protected readonly undefined = undefined;
}
